import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { z } from "zod";

import { ChooseLetterTemplateModal } from "components/ChooseLetterTemplateModal/ChooseLetterTemplateModal";
import { FormField } from "components/form/FormField/FormField";
import { RichEditor } from "components/RichEditor/RichEditor";
import { Button } from "components/ui/Button";
import { InsertPickerModal } from "components/ui/InsertPickerModal/InsertPickerModal";
import { useCreateLetter } from "features/templates/hooks/useCreateLetter";
import { formatPatientWithDateOfBirth } from "utils/dateFormatter";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { stripHtml } from "utils/stripHtml";

import style from "./AddLetterForm.module.css";
import { AudioRecorderSection } from "./components/AudioRecorderSection/AudioRecorderSection";
import { useInsertNote } from "./hooks/useInsertNote";
import { useInsertPrescription } from "./hooks/useInsertPrescription";
import { useLetterAudio } from "./hooks/useLetterAudio";
import { useLetterAutoSave } from "./hooks/useLetterAutoSave";
import { parseNoteDate } from "./utils/formatContent";

const formSchema = z.object({
  saveAsTemplate: z.boolean(),
  letter: z.string().min(1, { message: "Please provide content for letter." }),
  title: z.string().min(1, { message: "Please provide letter title." }),
  type: z.string().min(1, { message: "Please select letter type." }),
});

export const AddLetterForm = ({
  patient,
  onSuccess,
  consultationId,
  staffMemberId,
  initialData,
  autoSave,
  isReadOnly = false,
  onNewLetter,
  refetchLetters,
}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const savedId = useRef(initialData?.id || null);
  const savedDocumentId = useRef(initialData?.documentId || null);
  const savedDocumentUrl = useRef(initialData?.documentUrl || null);

  useEffect(() => {
    savedDocumentId.current = initialData?.documentId || null;
    savedDocumentUrl.current = initialData?.documentUrl || null;
  }, [initialData?.documentId, initialData?.documentUrl]);

  const initialContent = initialData?.text;

  const [letterContent, setLetterContent] = useState(initialContent);
  const [editorKey, setEditorKey] = useState(0);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      saveAsTemplate: false,
      letter: initialContent,
      title: initialData?.name || "",
      type: initialData?.letterType || "type-1",
    },
  });

  const createLetter = useCreateLetter();
  const createLetterRef = useRef(createLetter);
  createLetterRef.current = createLetter;

  const handleFormSubmit = useCallback(
    async (values) => {
      try {
        const res = await createLetterRef.current.mutateAsync({
          consultationId,
          id: savedId.current,
          patientId: patient.id,
          tenant: tenantVal,
          text: values.letter,
          name: values.title,
          letterType: values.type,
          ...(staffMemberId ? { staffMemberId } : {}),
          documentId: savedDocumentId.current,
          documentUrl: savedDocumentUrl.current,
        });
        savedId.current = res.id;
        savedDocumentId.current = res.documentId ?? savedDocumentId.current;
        savedDocumentUrl.current = res.documentUrl ?? savedDocumentUrl.current;
        refetchLetters?.();
        onSuccess?.();
      } catch (error) {
        console.error("Submission error:", error);
      }
    },
    [
      consultationId,
      staffMemberId,
      patient.id,
      tenantVal,
      onSuccess,
      refetchLetters,
    ]
  );

  useLetterAutoSave({ enabled: autoSave, form, onSave: handleFormSubmit });

  const appendToLetter = (content) => {
    const newContent = letterContent + content;
    setLetterContent(newContent);
    form.setValue("letter", newContent);
    setEditorKey((prev) => prev + 1);
  };

  const noteInsert = useInsertNote({
    patientId: patient?.id,
    onInsert: appendToLetter,
  });
  const prescriptionInsert = useInsertPrescription({
    patientId: patient?.id,
    onInsert: appendToLetter,
  });

  const audio = useLetterAudio({
    getLetterId: () => initialData?.id || savedId.current,
    tenantVal,
    initialData,
    onSaveBefore: async () => {
      const values = form.getValues();
      if (!values.title || !values.type) {
        toast.error(
          "Please fill in the title and type before recording audio."
        );
        return false;
      }
      await handleFormSubmit(values);
      return !!savedId.current;
    },
    onTranscribed: (data) => {
      setLetterContent(data.text);
      form.setValue("letter", data.text, { shouldDirty: true });
      setEditorKey((prev) => prev + 1);
    },
  });

  return (
    <FormProvider {...form}>
      <form
        className={style.addLetterForm}
        onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("AddLetterForm"))}
      >
        <div className={style.inputGroupGrid3}>
          <FormField name="title">
            <label className={style.addLetterFormLabel}>Title</label>
            <input
              type="text"
              className={`form-control ${
                isReadOnly ? style.readOnlyInput : ""
              }`}
              readOnly={isReadOnly}
              {...form.register("title")}
            />
          </FormField>
          <div>
            <label className={style.addLetterFormLabel}>Patient</label>
            <input
              type="text"
              className={`form-control ${style.readOnlyInput}`}
              value={
                formatPatientWithDateOfBirth(patient)
              }
              readOnly
            />
          </div>
        </div>

        <FormField name="letter">
          {!isReadOnly && (
            <div className={style.letterHeader}>
              <div className={style.letterHeaderLeft}>
                <ChooseLetterTemplateModal
                  patientId={patient?.id}
                  consultationId={consultationId}
                  onSelect={(templateContent, templateName) => {
                    setLetterContent(templateContent);
                    form.setValue("letter", templateContent);
                    form.setValue("title", templateName);
                    setEditorKey((prev) => prev + 1);
                  }}
                  isDisabled={audio.isProcessing}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="large"
                  onClick={noteInsert.openModal}
                  disabled={audio.isProcessing}
                >
                  Insert Note
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="large"
                  onClick={prescriptionInsert.openModal}
                  disabled={audio.isProcessing}
                >
                  Insert Prescription
                </Button>
              </div>
              <AudioRecorderSection
                isRecording={audio.isRecording}
                isProcessing={audio.isProcessing}
                startRecording={audio.startRecording}
                stopRecording={audio.stopRecording}
                audioUrl={audio.audioUrl}
                isPlaying={audio.isPlaying}
                audioRef={audio.audioRef}
                toggleAudioPlayback={audio.toggleAudioPlayback}
                hasAudioFile={!!initialData?.audioFilePath}
                onAudioEnded={() => audio.setIsPlaying(false)}
                onAudioPlay={() => audio.setIsPlaying(true)}
                onAudioPause={() => audio.setIsPlaying(false)}
              />
            </div>
          )}

          <RichEditor
            key={editorKey}
            editorValue={letterContent}
            onChange={(value) => {
              if (!audio.isProcessing && !isReadOnly) {
                setLetterContent(value);
                form.setValue("letter", value);
              }
            }}
            disabled={audio.isProcessing}
            readOnly={isReadOnly}
          />
        </FormField>

        {!isReadOnly && (
          <div className={style.bottomSection}>
            <label className={style.formCheckboxLabel}></label>
            <div className={style.buttonGroup}>
              <Button
                type="button"
                variant="outline"
                size="large"
                onClick={form.handleSubmit(async (values) => {
                  await handleFormSubmit(values);
                  savedId.current = null;
                  savedDocumentId.current = null;
                  savedDocumentUrl.current = null;
                  const newContent = "";
                  setLetterContent(newContent);
                  audio.setAudioUrl(null);
                  form.reset({
                    saveAsTemplate: false,
                    letter: newContent,
                    title: "",
                    type: "type-1",
                  });
                  setEditorKey((prev) => prev + 1);
                  onNewLetter?.();
                }, onInvalidSubmit("AddLetterForm"))}
              >
                New Letter
              </Button>
              <Button type="submit" variant="primary" size="large">
                Save Letter
              </Button>
            </div>
          </div>
        )}
      </form>

      <InsertPickerModal
        isOpen={noteInsert.showModal}
        onClose={noteInsert.closeModal}
        title="Select Note to Insert"
        search={noteInsert.search}
        onSearchChange={noteInsert.setSearch}
        searchPlaceholder="Search notes..."
        isLoading={noteInsert.isLoading}
        loadingText="Loading notes..."
        emptyText="No notes found"
        items={noteInsert.filtered}
        onSelect={noteInsert.handleInsert}
        renderItem={(note) => (
          <>
            <div className={style.listItemDate}>
              {(() => {
                const parsed = parseNoteDate(note.startTime || note.created);
                return parsed.isValid()
                  ? parsed.format("DD-MM-YYYY HH:mm")
                  : "Date unknown";
              })()}
            </div>
            <div className={style.listItemContent}>
              {(() => {
                const plain = stripHtml(note.note);
                return (
                  plain.substring(0, 150) + (plain.length > 150 ? "..." : "")
                );
              })()}
            </div>
          </>
        )}
      />

      <InsertPickerModal
        isOpen={prescriptionInsert.showModal}
        onClose={prescriptionInsert.closeModal}
        title="Select Prescription to Insert"
        search={prescriptionInsert.search}
        onSearchChange={prescriptionInsert.setSearch}
        searchPlaceholder="Search prescriptions..."
        isLoading={prescriptionInsert.isLoading}
        loadingText="Loading prescriptions..."
        emptyText="No prescriptions found"
        items={prescriptionInsert.filtered}
        onSelect={prescriptionInsert.handleInsert}
        renderItem={(prescription) => (
          <>
            <div className={style.listItemDate}>
              {moment(
                prescription.datePrescribed || prescription.created
              ).format("DD-MM-YYYY")}
              {" — "}
              {prescription.medications?.length || 0} medication(s)
            </div>
            <div className={style.listItemContent}>
              {prescription.note || "No note"}
            </div>
          </>
        )}
      />
    </FormProvider>
  );
};
